import React from 'react';
import { productName, logoSrc, logoStyleCompact } from '../brand';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import PageFooter from '../components/chrome/PageFooter';

export default function SignIn({ onSignIn, onSignInWithEmail, onSignUp, onLanding }) {
  return (
    <div style={pageStyle}>
      <div style={twoColStyle}>
        <div style={leftPanelStyle}>
          <button type="button" onClick={onLanding} style={logoButtonStyle} aria-label="Back to landing">
            <img src={logoSrc} alt={productName} style={logoStyleCompact} />
          </button>

          <div style={formStackStyle}>
            <div>
              <div style={eyebrowStyle}>Welcome back</div>
              <h1 style={heroStyle}>
                Sign in to your <em style={accentItalicStyle}>valuations.</em>
              </h1>
              <p style={ledeStyle}>Pick up a draft, share a result, or start a fresh one.</p>
            </div>

            <GoogleSignInButton onClick={onSignIn} />

            {onSignInWithEmail && (
              <button type="button" onClick={onSignInWithEmail} style={emailButtonStyle}>
                Or sign in with email
              </button>
            )}

            <p style={altStyle}>
              Don't have an account?{' '}
              <button type="button" onClick={onSignUp} style={inlineLinkStyle}>Create one</button>
            </p>

            <div style={trustStripStyle}>
              <span>Google SSO</span>
              <span style={trustDotStyle}>·</span>
              <span>Auth0-brokered</span>
              <span style={trustDotStyle}>·</span>
              <span style={{ color: 'var(--accent)', fontWeight: 500 }}>Zero PHI custody</span>
            </div>
          </div>
        </div>

        <div style={rightPanelStyle}>
          <div style={{ ...eyebrowStyle, color: 'var(--amber-strong)', marginBottom: '14px' }}>From the calibration set</div>
          <h2 style={rightHeroStyle}>
            55+ healthcare data deals indexed.{' '}
            <em style={accentItalicStyle}>Six tiers, three revenue models.</em>
          </h2>

          <div style={statsCardStyle}>
            <div style={statsLabelStyle}>Reference deals indexed</div>
            <div style={statsBigNumberStyle}>55+</div>
            <div style={statsSplitStyle}>
              <div>
                <div style={statsSubLabelStyle}>Highest / patient</div>
                <div style={statsSubValueStyle}>$4,321</div>
                <div style={statsSubMutedStyle}>NIH All of Us</div>
              </div>
              <div>
                <div style={statsSubLabelStyle}>Median tier</div>
                <div style={statsSubValueStyle}>Standard</div>
                <div style={statsSubMutedStyle}>across the index</div>
              </div>
            </div>
          </div>

          <p style={fineStyle}>
            Estimates only — grounded in 55+ documented transactions; not an appraisal, guarantee, or legal
            advice.
          </p>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}

const pageStyle = { minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)', color: 'var(--ink)', display: 'flex', flexDirection: 'column' };
const twoColStyle = { display: 'grid', gridTemplateColumns: 'minmax(420px, 1fr) 1fr', flex: 1, maxWidth: '1280px', margin: '0 auto', width: '100%' };

const leftPanelStyle = { padding: '48px 56px', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--line)' };
const logoButtonStyle = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', alignSelf: 'flex-start' };

const formStackStyle = { marginTop: '64px', display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '460px' };

const eyebrowStyle = { fontSize: '13px', color: 'var(--accent)', fontWeight: 500 };
const heroStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: '44px',
  letterSpacing: '-0.02em',
  lineHeight: 1.05,
  margin: '8px 0 0',
  color: 'var(--ink)',
};
const accentItalicStyle = { fontStyle: 'italic', color: 'var(--accent)' };
const ledeStyle = { fontSize: '15px', color: 'var(--ink2)', lineHeight: 1.55, margin: '14px 0 0' };

const altStyle = { fontSize: '13px', color: 'var(--muted)', margin: 0 };
const inlineLinkStyle = { background: 'transparent', border: 'none', color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 };

const trustStripStyle = { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '11.5px', color: 'var(--muted)', marginTop: '4px' };
const trustDotStyle = { color: 'var(--muted2)' };

const rightPanelStyle = { padding: '88px 56px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' };
const rightHeroStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: '36px',
  letterSpacing: '-0.025em',
  lineHeight: 1.1,
  maxWidth: '460px',
  margin: 0,
  color: 'var(--ink)',
};

const statsCardStyle = { marginTop: '28px', padding: '24px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '8px', maxWidth: '460px' };
const statsLabelStyle = { fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' };
const statsBigNumberStyle = {
  fontFamily: 'var(--display)',
  fontSize: '64px',
  lineHeight: 1,
  fontStyle: 'italic',
  fontWeight: 500,
  color: 'var(--accent)',
  letterSpacing: '-0.03em',
};
const statsSplitStyle = { marginTop: '18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', borderTop: '1px solid var(--line)', paddingTop: '14px' };
const statsSubLabelStyle = { fontSize: '11.5px', color: 'var(--muted)' };
const statsSubValueStyle = { fontSize: '18px', fontWeight: 600, marginTop: '2px' };
const statsSubMutedStyle = { fontSize: '11px', color: 'var(--muted)', marginTop: '2px' };

const fineStyle = { marginTop: '24px', fontSize: '11.5px', color: 'var(--muted)', fontStyle: 'italic', lineHeight: 1.55, maxWidth: '440px' };

const emailButtonStyle = {
  background: 'transparent',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--ink2)',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '14px',
  padding: '10px 16px',
  textAlign: 'center',
  width: '100%',
};
